import { AfterViewInit, ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, forwardRef, inject, input, InputSignal, Signal, viewChild } from '@angular/core';
import { FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Loader } from '@googlemaps/js-api-loader';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RestService } from 'src/app/services/rest.service';
import { RegisterUserFormService } from '../../../services/register-user-form.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BaseFormField } from '../../form/abstracts';
import { closeAllCiSnackbar } from '@iurban-malaga/cicerone-ui';
import { BreakpointsService } from 'src/app/services/breakpoints.service';


@Component({
  selector: 'app-location-field',
  standalone: true,
  imports: [
    TranslateModule, 
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationFieldComponent),
      multi:true,
    },
  ],
  templateUrl: './location-field.component.html',
  styleUrls: ['./location-field.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationFieldComponent extends BaseFormField<string> implements AfterViewInit {
  public inputLocation: Signal<ElementRef | undefined> = viewChild('inputLocation');
  public switchLocation: Signal<ElementRef | undefined> = viewChild('switchLocation');
  
  public form: InputSignal<FormGroup> = input.required<FormGroup>();
  public formControlName: InputSignal<string> = input.required<string>();
  public isNextButtonClicked: InputSignal<boolean> = input<boolean>();
  
  private _translateService = inject(TranslateService);
  private _restService = inject(RestService);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _breakpointsService = inject(BreakpointsService);
  
  private _startLocation = this._registerUserFormService.startLocation;
  private _isMobile = this._breakpointsService.isMobile;
  private _formControlName = computed(() => this.form().get(this.formControlName()));
  private _markAllFormFieldsAsTouched = this._registerUserFormService.markAllFormFieldsAsTouched;
  public isInvalidFieldSignal = computed(() => {
    const control = this._formControlName();
    const shouldMarkAsTouched = this._markAllFormFieldsAsTouched();
    
    if (this._startLocation()?.address) return false;
    
    if (this._isMobile()) {
      // mobile: check only when next button is clicked
      if (this.isNextButtonClicked()) {
        return control?.hasError('required') && (control?.touched || control?.dirty);
      } else {
        return false;
      }
    } else {
      // desktop: normal behavior
      return control?.hasError('required') && (control?.touched || shouldMarkAsTouched);
    }
  });
  
  // Override writeValue para manejar cuando el FormControl cambia
  override writeValue(value: string): void {
    super.writeValue(value);

    // Si el valor viene del FormControl y es diferente al del servicio, sincronizar
    if (value && value !== this._startLocation()?.address) {
      const currentLocation = this._startLocation();
      this._registerUserFormService.setStartLocation({
        lat: currentLocation?.lat || 0,
        lng: currentLocation?.lng || 0,
        address: value
      });
    }
  }
  
  updateLocationValues = effect(() => {
    const locationAddress = this._startLocation()?.address || '';
    
    // Solo actualizar si el valor ha cambiado
    if (this.value !== locationAddress) {
      this.value = locationAddress;
      
      // Actualizar el FormControl del parent component
      const control = this.form().get(this.formControlName());
      if (control && control.value !== locationAddress) {
        control.setValue(locationAddress, { emitEvent: false });
      }
      
      // Notificar el cambio a través del ControlValueAccessor
      this.onChange(locationAddress);
    }
  }, { allowSignalWrites: true });
  
  ngAfterViewInit(){
    const input = this.inputLocation()?.nativeElement;
    if (input){
      input.placeholder = this._translateService.instant('Seleccionar punto de inicio');
      const center = this._restService.device.location;
    
      // 6km search radius
      const defaultBounds = { north: center.latitude + 0.06, south: center.latitude - 0.06, east: center.longitude + 0.06, west: center.longitude - 0.06 };
      const options = { bounds: defaultBounds, strictBounds: true, types: [], componentRestrictions: {country: ['es', 'and', 'br'] } };

      const loader = new Loader({
          apiKey: "AIzaSyCosmIo9py_6Dfd_mr1CWSJxQtDzl1LWjA",
          version: "weekly",
          libraries: ["geometry", "places"]
      });
      
      loader.load().then(async () => {
        const { Autocomplete } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;
        const searchBox = new Autocomplete(input, options);     
        
        searchBox?.addListener('place_changed', async () => {
          const place = searchBox.getPlace();
          const location = await this.getLocation(place);
          
          // Set the location in the form service
          this._registerUserFormService.setStartLocation({
            lat: location.latitude,
            lng: location.longitude,
            address: location.name
          });
          
          // Actualizar el valor usando BaseFormField
          this.value = location.name;
          this.onChange(this.value);
          this.markAsTouched();
          
          // Set the values in the form
          this._formControlName()?.setValue(location.name, { emitEvent: false });
          this.form().get('startLocationLat')?.setValue(location.latitude);
          this.form().get('startLocationLng')?.setValue(location.longitude);

          if (this._isMobile()) {
            closeAllCiSnackbar(); // close snackbar if the location is valid (UX mobile)
          }
        });
      });
    }
  }
  
  public getLocation(place: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (place && place.geometry && place.geometry.location) {
        const latitudeForm = place.geometry.location.lat();
        const longitudeForm = place.geometry.location.lng();
        const location = { 'latitude': latitudeForm, 'longitude': longitudeForm, 'name': place.name };
        
        resolve(location);
      } else {
        reject(new Error('No se pudo obtener la geolocalización del lugar seleccionado.'));
      }
    });
  }
  
  public focusin(event: Event) {
    event.stopPropagation();
    this.markAsTouched(); // Marcar como touched cuando recibe focus
  }
  
  public getCurrentStartLocation(): string {
    // Usar el valor de BaseFormField que se sincroniza con el FormControl
    return this.value || this._startLocation()?.address || '';
  }

  public getCurrentEndLocation(): boolean {
    return this._registerUserFormService.endLocation();
  }
  
  public isDisabledEndLocationSwitch(): boolean {
    return !this.form().get('startLocationAddress')?.value;
  }
  
  public handleInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const inputValue = target.value;
    
    // Actualizar el valor usando BaseFormField
    this.value = inputValue;
    this.onChange(inputValue);
    this.markAsTouched();
    
    this._formControlName()?.setValue(inputValue, { emitEvent: false });
    this._formControlName()?.markAsTouched();
    
    // Si el campo se vacía, limpiar también el servicio
    if (!inputValue || inputValue.trim() === '') {
      this._registerUserFormService.setStartLocation(null);
      
      // Limpiar también lat/lng
      this.form().get('startLocationLat')?.setValue(null);
      this.form().get('startLocationLng')?.setValue(null);
    }
  }
    
  public handleSwitchLocationChange(event: Event) {
    const customEvent = event as CustomEvent<{ checked: boolean }>;
    const isChecked = customEvent.detail.checked;
    this._registerUserFormService.setEndLocation(isChecked);
    this.form().get('endLocation')?.setValue(isChecked);
  }
}
